// [LIVE-VENTES] Modal détail vendeur
import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "../../App";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, TrendingUp, ShoppingBag, Clock, Tag, Percent, BarChart3, Loader2 } from "lucide-react";

// [LIVE-VENTES] Onglets période
const PERIODS = [
  { id: "today", label: "Aujourd'hui" },
  { id: "week", label: "7 jours" },
  { id: "month", label: "Ce mois" }
];

export default function VendeurDetailModal({ vendeur, open, onClose }) {
  const { getAuthHeaders } = useAuth();
  const [period, setPeriod] = useState("today");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const boutiqueData = JSON.parse(localStorage.getItem("boutique") || "{}");
  const devise = boutiqueData.currencySymbol || "€";
  const accentColor = boutiqueData.customColor || boutiqueData.colorPalette?.[0] || "#D4A853";

  const fetchStats = useCallback(async () => {
    if (!vendeur?.id) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/live-sales/vendeur/${vendeur.id}?period=${period}`,
        getAuthHeaders()
      );
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [vendeur?.id, period, getAuthHeaders]);

  useEffect(() => {
    if (open && vendeur?.id) fetchStats();
  }, [open, vendeur?.id, period, fetchStats]);

  if (!open) return null;

  const isOwner = vendeur?.role === "owner";
  const avatarBg = isOwner
    ? `linear-gradient(135deg, ${accentColor} 0%, ${accentColor}99 100%)`
    : "linear-gradient(135deg, #1E3A5F 0%, #2563EB 100%)";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="vendeur-detail-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          style={{
            position: "fixed", inset: 0, zIndex: 9999,
            background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 16
          }}
        >
          <motion.div
            data-testid="vendeur-detail-modal"
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={e => e.stopPropagation()}
            style={{
              background: "#1A1A1A", borderRadius: 16, width: "100%",
              maxWidth: 560, maxHeight: "85vh", overflow: "auto",
              border: "1px solid #2A2A2A"
            }}
          >
            {/* Header */}
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "20px 24px", borderBottom: "1px solid #2A2A2A"
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 10, background: avatarBg,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 16, fontWeight: 700, color: "#fff"
                }}>
                  {(vendeur?.prenom || "?").substring(0, 2).toUpperCase()}
                </div>
                <div>
                  <div style={{ color: "#fff", fontWeight: 700, fontSize: 16 }}>
                    Activit&eacute; de {vendeur?.prenom || "Vendeur"}
                  </div>
                  <div style={{ color: "#9CA3AF", fontSize: 12 }}>
                    {isOwner ? "Propri\u00e9taire" : "Vendeur"}
                  </div>
                </div>
              </div>
              <button
                data-testid="close-vendeur-modal"
                onClick={onClose}
                style={{
                  width: 32, height: 32, borderRadius: 8,
                  background: "#2A2A2A", border: "none",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  cursor: "pointer", color: "#9CA3AF"
                }}
              >
                <X size={16} />
              </button>
            </div>

            {/* Period tabs */}
            <div style={{
              display: "flex", gap: 8, padding: "16px 24px",
              borderBottom: "1px solid #2A2A2A"
            }}>
              {PERIODS.map(p => (
                <button
                  key={p.id}
                  data-testid={`period-tab-${p.id}`}
                  onClick={() => setPeriod(p.id)}
                  style={{
                    padding: "6px 14px", borderRadius: 8, fontSize: 13, fontWeight: 600,
                    background: period === p.id ? accentColor : "#2A2A2A",
                    color: period === p.id ? "#fff" : "#9CA3AF",
                    border: "none", cursor: "pointer",
                    transition: "all 200ms"
                  }}
                >
                  {p.label}
                </button>
              ))}
            </div>

            {/* Content */}
            <div style={{ padding: "20px 24px" }}>
              {loading ? (
                <div style={{ display: "flex", justifyContent: "center", padding: 32 }}>
                  <Loader2 size={24} className="animate-spin" style={{ color: accentColor }} />
                </div>
              ) : !data || data.nb_ventes === 0 ? (
                <div style={{ color: "#6B7280", fontSize: 14, textAlign: "center", padding: "32px 0" }}>
                  Aucune vente sur cette p&eacute;riode.
                </div>
              ) : (
                <>
                  {/* Stats grid */}
                  <div style={{
                    display: "grid", gridTemplateColumns: "repeat(3, 1fr)",
                    gap: 12, marginBottom: 20
                  }}>
                    {[
                      { icon: ShoppingBag, label: "Ventes", value: data.nb_ventes, color: "#22C55E" },
                      { icon: TrendingUp, label: "CA Total", value: `${data.total_ca?.toLocaleString("fr-FR")} ${devise}`, color: accentColor },
                      { icon: BarChart3, label: "Panier moyen", value: `${data.panier_moyen?.toLocaleString("fr-FR")} ${devise}`, color: "#3B82F6" },
                    ].map((stat, i) => (
                      <div key={i} style={{
                        background: "#111", borderRadius: 12, padding: "14px 12px",
                        border: "1px solid #2A2A2A", textAlign: "center"
                      }}>
                        <stat.icon size={18} style={{ color: stat.color, margin: "0 auto 6px" }} />
                        <div style={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>{stat.value}</div>
                        <div style={{ color: "#6B7280", fontSize: 11, marginTop: 2 }}>{stat.label}</div>
                      </div>
                    ))}
                  </div>

                  {/* Additional stats */}
                  <div style={{
                    display: "grid", gridTemplateColumns: "repeat(3, 1fr)",
                    gap: 12, marginBottom: 20
                  }}>
                    {[
                      { icon: Tag, label: "Top article", value: data.article_top || "-", color: "#F59E0B" },
                      { icon: Clock, label: "Heure pic", value: data.heure_pic || "-", color: "#8B5CF6" },
                      { icon: Percent, label: "Remises", value: `${data.taux_remises}%`, color: "#EF4444" },
                    ].map((stat, i) => (
                      <div key={i} style={{
                        background: "#111", borderRadius: 12, padding: "14px 12px",
                        border: "1px solid #2A2A2A", textAlign: "center"
                      }}>
                        <stat.icon size={18} style={{ color: stat.color, margin: "0 auto 6px" }} />
                        <div style={{ color: "#fff", fontWeight: 700, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{stat.value}</div>
                        <div style={{ color: "#6B7280", fontSize: 11, marginTop: 2 }}>{stat.label}</div>
                      </div>
                    ))}
                  </div>

                  {/* Sales table */}
                  <div style={{ borderTop: "1px solid #2A2A2A", paddingTop: 16 }}>
                    <div style={{
                      color: "#9CA3AF", fontSize: 13, textTransform: "uppercase",
                      letterSpacing: 0.5, fontWeight: 600, marginBottom: 12
                    }}>
                      D&eacute;tail des ventes
                    </div>
                    <div style={{ maxHeight: 240, overflow: "auto" }}>
                      {/* Table header */}
                      <div style={{
                        display: "grid", gridTemplateColumns: "1.5fr 1fr 0.8fr 0.8fr",
                        gap: 8, padding: "8px 0", borderBottom: "1px solid #2A2A2A",
                        position: "sticky", top: 0, background: "#1A1A1A"
                      }}>
                        {["Article", "Taille", "Montant", "Paiement"].map(h => (
                          <div key={h} style={{ color: "#6B7280", fontSize: 11, fontWeight: 600, textTransform: "uppercase" }}>{h}</div>
                        ))}
                      </div>
                      {data.ventes?.map((v, i) => (
                        <div key={i} style={{
                          display: "grid", gridTemplateColumns: "1.5fr 1fr 0.8fr 0.8fr",
                          gap: 8, padding: "8px 0", borderBottom: "1px solid #1F1F1F",
                          fontSize: 13
                        }}>
                          <div style={{ color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v.article}</div>
                          <div style={{ color: "#9CA3AF" }}>{v.taille || "-"}</div>
                          <div style={{ color: "#fff", fontWeight: 600 }}>{(v.montant || 0).toLocaleString("fr-FR")} {devise}</div>
                          <div style={{ color: "#9CA3AF", textTransform: "capitalize" }}>{v.paiement}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Total */}
                  <div style={{
                    marginTop: 16, padding: "14px 16px", borderRadius: 12,
                    background: "#111", border: `1px solid ${accentColor}33`,
                    display: "flex", justifyContent: "space-between", alignItems: "center"
                  }}>
                    <span style={{ color: "#9CA3AF", fontSize: 13 }}>
                      Total encaiss&eacute; par {data.prenom}
                    </span>
                    <span style={{ color: accentColor, fontWeight: 700, fontSize: 16 }}>
                      {data.total_ca?.toLocaleString("fr-FR")} {devise}
                    </span>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
